import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { NewsletterTemplate, NewsletterTemplateSummary } from '../types/models/NewsletterTemplate';
import type { ApiResponseNewsletterTemplateList } from '../types/api/ApiResponseNewsletterTemplate';

interface UseNewsletterTemplatesReturn {
  templates: NewsletterTemplateSummary[];
  /** True while any fetch is in flight (initial load OR a view switch / manual refresh). A same-view
   *  refresh keeps the current rows on screen; a VIEW SWITCH clears them first, so gate a full-panel
   *  spinner on `loading && templates.length === 0`. */
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Upsert a created/updated template into the CURRENT view (matched by `id`) so a save updates the
   * table without a full refetch. A row only belongs to the view whose archived state it matches:
   *   • an edit keeps `is_archived` unchanged → the row is updated in place;
   *   • a fresh create is always active → prepended (newest-first) only while the Active view shows;
   *   • a row that no longer belongs to the current view is dropped (defensive).
   */
  applyTemplate: (updated: NewsletterTemplate) => void;
  /** Drop one template row (used after archiving from the Active view). */
  removeTemplate: (id: number) => void;
}

/** Byte length of a UTF-8 string — mirrors the backend's `twig_bytes` for optimistic list updates. */
const byteLength = (value: string): number => new TextEncoder().encode(value).length;

/** Collapse a full template (create/update response) into the lighter list-summary shape. */
const toSummary = (full: NewsletterTemplate): NewsletterTemplateSummary => ({
  id: full.id,
  name: full.name,
  is_archived: full.is_archived,
  twig_bytes: byteLength(full.twig),
  archived_at: full.archived_at,
  created_at: full.created_at,
  updated_at: full.updated_at,
});

/**
 * Fetches the newsletter TEMPLATE list for the Ancalagon-staff-only Newsletter admin page
 * (`GET /admin/newsletters/templates`). The `archived` flag selects the server-side set — the
 * active set (`archived_at IS NULL`) by default, or the archived set with `?archived=1` — so
 * toggling it refetches. Rows come back newest-first as lightweight summaries (no `twig` body).
 *
 * The endpoint returns the whole set in one call (no server-side search/pagination params), so the
 * consumer searches and paginates client-side. Per the API contract the backend wraps responses in
 * JSend and maps failures to `status: 'error'` at HTTP 200, so this branches on
 * `body.status === 'success'`, never `response.ok`.
 *
 * The parent page is Ancalagon-staff-gated (renders <NotFound /> otherwise) and mounts the tab
 * lazily via `<Tabs mountOnEnter>`, so the fetch is already deferred to staff who open the tab.
 */
export const useNewsletterTemplates = (archived: boolean): UseNewsletterTemplatesReturn => {
  const [templates, setTemplates] = useState<NewsletterTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/newsletters/templates${archived ? '?archived=1' : ''}`);
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterTemplateList | null;
      if (body?.status === 'success' && Array.isArray(body.data?.newsletter_templates)) {
        setTemplates(body.data.newsletter_templates);
      } else {
        setError(body?.message ?? 'Failed to load newsletter templates.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching newsletter templates.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, archived]);

  const applyTemplate = useCallback(
    (updated: NewsletterTemplate) => {
      const summary = toSummary(updated);
      setTemplates((prev) => {
        const belongs = updated.is_archived === archived;
        const exists = prev.some((t) => t.id === updated.id);
        if (belongs) {
          return exists ? prev.map((t) => (t.id === updated.id ? summary : t)) : [summary, ...prev];
        }
        return exists ? prev.filter((t) => t.id !== updated.id) : prev;
      });
    },
    [archived],
  );

  const removeTemplate = useCallback((id: number) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Switching the Active/Archived set makes the currently-held rows belong to the OTHER view — drop
  // them immediately so a failed (or in-flight) switch never shows the previous view's rows under
  // the new label. A same-view refresh doesn't change `archived`, so its rows are kept on screen.
  useEffect(() => {
    setTemplates([]);
  }, [archived]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchTemplates();
    }
  }, [auth.isAuthenticated, fetchTemplates]);

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
    applyTemplate,
    removeTemplate,
  };
};

export default useNewsletterTemplates;
